import React from 'react'

type TermsSection = {
  title: string
  paragraphs?: string[]
  bullets?: string[]
}

const sections: TermsSection[] = [
  {
    title: '1. Acceptance of Terms',
    paragraphs: [
      'By accessing or using LogiSelect ("Service"), you agree to be bound by these Terms and Conditions. If you do not agree to these Terms, you must not use the Service.',
    ],
  },
  {
    title: '2. About LogiSelect',
    paragraphs: [
      'LogiSelect is an online logistics and transport planning platform that enables users to:',
    ],
    bullets: [
      'Create, manage and submit consignments',
      'Manage carriers, rates and routing selections',
      'Access consignment tracking and reporting information',
      'Exchange operational data with authorised third-party systems through APIs',
    ],
  },
  {
    title: '3. User Eligibility',
    paragraphs: ['You represent and warrant that:'],
    bullets: [
      'You are authorised to act on behalf of your organisation.',
      'All information provided is accurate and complete.',
      'You will use the Service only for lawful business purposes.',
    ],
  },
  {
    title: '4. User Accounts',
    paragraphs: [
      'Users are responsible for:',
      'LogiSelect reserves the right to suspend or terminate accounts where misuse is detected.',
    ],
    bullets: [
      'Maintaining the confidentiality of login credentials.',
      'Restricting access to authorised personnel.',
      'Ensuring account information remains current.',
      'Promptly reporting any suspected unauthorised access.',
    ],
  },
  {
    title: '5. Service Availability',
    paragraphs: ['While LogiSelect aims to provide continuous service availability:'],
    bullets: [
      'Availability is not guaranteed.',
      'Planned maintenance may occur periodically.',
      'Service interruptions may occur due to factors outside LogiSelect\'s control.',
      'Emergency maintenance may be performed without prior notice.',
    ],
  },
  {
    title: '6. TMS and Third-Party Integrations',
    paragraphs: [
      'LogiSelect may exchange data with:',
      'Users acknowledge that:',
    ],
    bullets: [
      'Transport Management Systems (TMS)',
      'Warehouse Management Systems (WMS)',
      'Enterprise Resource Planning (ERP) systems',
      'Carrier platforms',
      'Mapping and routing providers',
      'Government and regulatory systems',
      'Third-party systems are operated independently.',
      'Data accuracy depends on information supplied by integrated systems.',
      'LogiSelect is not responsible for outages or failures of third-party services.',
    ],
  },
  {
    title: '7. Customer Data',
    paragraphs: [
      'The Customer retains ownership of all data submitted to the Service.',
      'The Customer grants LogiSelect permission to:',
    ],
    bullets: [
      'Store and process Customer Data',
      'Perform system integrations',
      'Generate operational reports',
      'Maintain backups',
      'Provide support services',
      'Improve system performance and security',
    ],
  },
  {
    title: '8. Data Accuracy',
    paragraphs: [
      'Users are responsible for ensuring the accuracy of:',
      'LogiSelect is not liable for losses resulting from inaccurate or incomplete data submitted by users.',
    ],
    bullets: [
      'Consignment details',
      'Addresses',
      'Product information',
      'Dangerous goods declarations',
      'Carrier instructions',
      'Customs and compliance information',
    ],
  },
  {
    title: '9. Acceptable Use',
    paragraphs: ['Users must not:'],
    bullets: [
      'Attempt unauthorised access to systems or data',
      'Interfere with platform operation',
      'Upload malicious code or software',
      'Use the Service for unlawful activity',
      'Copy or redistribute proprietary information without permission',
      'Reverse engineer or decompile the platform',
    ],
  },
  {
    title: '10. Intellectual Property',
    paragraphs: [
      'All intellectual property rights in the Service, including:',
      'remain the exclusive property of LogiSelect or its licensors.',
      'No ownership rights are transferred to users.',
    ],
    bullets: [
      'Software',
      'Databases',
      'Documentation',
      'Branding',
      'Workflows',
      'User interfaces',
    ],
  },
  {
    title: '11. Fees and Payment',
    paragraphs: ['Where applicable:'],
    bullets: [
      'Subscription fees are payable in accordance with the applicable agreement.',
      'Additional transaction or usage charges may apply.',
      'Invoices must be paid within the agreed payment terms.',
      'Non-payment may result in suspension of access.',
    ],
  },
  {
    title: '12. Security',
    paragraphs: [
      'LogiSelect implements reasonable technical and organisational measures to protect Customer Data, including:',
      'Users remain responsible for securing their own devices and credentials.',
    ],
    bullets: [
      'User authentication controls',
      'Access management',
      'Encrypted communications',
      'Audit logging',
      'Backup and recovery procedures',
    ],
  },
  {
    title: '13. Privacy',
    paragraphs: [
      'Personal information is handled in accordance with the LogiSelect Privacy Policy.',
      'By using the Service, you consent to the collection, processing and storage of information required to deliver the Service.',
    ],
  },
  {
    title: '14. Limitation of Liability',
    paragraphs: ['To the maximum extent permitted by law:'],
    bullets: [
      'LogiSelect shall not be liable for indirect, incidental or consequential damages.',
      'LogiSelect shall not be responsible for losses arising from carrier actions, customs delays, network outages or third-party system failures.',
      'Total liability shall not exceed the fees paid by the Customer during the preceding six (6) months.',
    ],
  },
  {
    title: '15. Indemnification',
    paragraphs: ['The Customer agrees to indemnify and hold harmless LogiSelect against claims arising from:'],
    bullets: [
      'Improper use of the Service',
      'Breach of these Terms',
      'Inaccurate or unlawful customer data',
      'Violation of third-party rights',
    ],
  },
  {
    title: '16. Suspension and Termination',
    paragraphs: [
      'LogiSelect may suspend or terminate access where:',
      'Upon termination, access to the Service may be removed and Customer Data retained only as required by law or contractual obligations.',
    ],
    bullets: [
      'Fees remain unpaid.',
      'Security risks are identified.',
      'Applicable laws are breached.',
      'The Customer materially breaches these Terms.',
    ],
  },
  {
    title: '17. Confidentiality',
    paragraphs: [
      'Both parties agree to maintain the confidentiality of non-public business, technical and commercial information obtained through the use of the Service.',
    ],
  },
  {
    title: '18. Force Majeure',
    paragraphs: ['Neither party shall be liable for delays or failures caused by events beyond reasonable control, including:'],
    bullets: [
      'Natural disasters',
      'Telecommunications failures',
      'Cybersecurity incidents',
      'Government actions',
      'Industrial disputes',
    ],
  },
  {
    title: '19. Changes to Terms',
    paragraphs: [
      'LogiSelect reserves the right to update these Terms at any time.',
      'Continued use of the Service following publication of revised Terms constitutes acceptance of those changes.',
    ],
  },
  {
    title: '20. Governing Law',
    paragraphs: [
      'These Terms shall be governed by the laws of Victoria, Australia.',
      'Any dispute arising from these Terms shall be subject to the exclusive jurisdiction of the courts of Victoria, Australia.',
    ],
  },
  {
    title: '21. Contact Information',
    paragraphs: ['LogiSelect Support'],
    bullets: [
      'Email: support@logiselect.com.au',
      'Website: www.logiselect.com.au',
    ],
  },
]

export default function TermsAndConditionsPage() {
  const WEBSITE_BASE_URL = String(import.meta.env.VITE_WEBSITE_BASE_URL || 'https://logiselect.com.au').replace(/\/$/, '')
  const TMS_BASE_URL = String(import.meta.env.VITE_TMS_BASE_URL || 'https://logiselect.net').replace(/\/$/, '')
  const tmsLoginUrl = `${TMS_BASE_URL}/login?autologin=1`
  const ownershipFooterText = '© 2026 LogiSelect Pty Ltd. All content on this website and all TMS software are owned by LogiSelect Pty Ltd.'

  const pageStyle: React.CSSProperties = {
    minHeight: '100vh',
    background: 'linear-gradient(180deg, #f6f7f3 0%, #ffffff 42%, #eef2f6 100%)',
    color: '#0f172a',
    position: 'relative',
  }

  const largeLogoPanel: React.CSSProperties = {
    position: 'absolute',
    left: 'max(18px, calc((100vw - 1200px) / 2 - 180px))',
    top: 18,
    width: 160,
    background: '#ffffff',
    border: '1px solid #dbe3ea',
    borderRadius: 20,
    boxShadow: '0 16px 34px rgba(15, 23, 42, 0.09)',
    padding: '22px 16px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  }

  const shellStyle: React.CSSProperties = {
    width: 'min(1200px, calc(100% - 32px))',
    margin: '0 auto',
    padding: '24px 0 48px',
  }

  const heroGrid: React.CSSProperties = {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1.4fr) minmax(320px, 420px)',
    gap: 28,
    alignItems: 'start',
  }

  const heroCard: React.CSSProperties = {
    background: 'radial-gradient(circle at top left, rgba(180, 213, 190, 0.55), rgba(255,255,255,0.96) 42%), linear-gradient(135deg, #fff 0%, #f3f6f8 100%)',
    border: '1px solid rgba(148, 163, 184, 0.25)',
    borderRadius: 28,
    padding: '36px 34px',
    boxShadow: '0 22px 50px rgba(15, 23, 42, 0.08)',
  }

  const sectionCard: React.CSSProperties = {
    background: '#ffffff',
    border: '1px solid #e2e8f0',
    borderRadius: 24,
    padding: '28px 28px 30px',
    boxShadow: '0 16px 34px rgba(15, 23, 42, 0.06)',
  }

  const sideCard: React.CSSProperties = {
    background: '#fff',
    borderRadius: 24,
    padding: '28px 28px',
    border: '1px solid #dbe3ea',
    boxShadow: '0 24px 50px rgba(15, 23, 42, 0.12)',
    alignSelf: 'start',
  }

  const ctaPrimary: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '13px 18px',
    borderRadius: 999,
    background: '#16324f',
    color: '#ffffff',
    textDecoration: 'none',
    fontWeight: 700,
    border: 'none',
    cursor: 'pointer',
    minWidth: 180,
  }

  return (
    <div style={pageStyle} className="pw-page">
      <div style={largeLogoPanel} className="pw-logo-panel">
        <img src="/logiselect-logo.png" alt="LogiSelect logo" style={{ width: '100%', height: 'auto', maxWidth: 250, display: 'block' }} />
      </div>
      <div style={shellStyle} className="pw-shell">
        <div style={{ marginBottom: 18 }}>
          <a href={WEBSITE_BASE_URL} style={{ color: '#16324f', textDecoration: 'underline', fontWeight: 700, fontSize: 14 }}>
            Back to main website
          </a>
        </div>
        <div style={heroGrid} className="pw-hero-grid">
          <div style={{ display: 'grid', gap: 24 }}>
            <section style={heroCard}>
              <h1 style={{ margin: 0, fontSize: 'clamp(2rem, 3.8vw, 3.6rem)', lineHeight: 1.02, letterSpacing: '-0.04em', maxWidth: 760 }}>
                LogiSelect Terms and Conditions
              </h1>
              <p style={{ margin: '18px 0 0', fontSize: 18, lineHeight: 1.65, color: '#334155', maxWidth: 760 }}>
                Effective Date: 1 August 2026
              </p>
              <p style={{ margin: '12px 0 0', fontSize: 16, lineHeight: 1.7, color: '#475569', maxWidth: 760 }}>
                These terms govern the use of the LogiSelect platform, including access, service availability, data handling, integrations, payment, liability, and general legal conditions.
              </p>
            </section>

            {sections.map((section) => (
              <section key={section.title} style={sectionCard}>
                <h2 style={{ margin: 0, fontSize: 28, letterSpacing: '-0.03em' }}>{section.title}</h2>
                {section.paragraphs?.map((paragraph) => (
                  <p key={paragraph} style={{ margin: '16px 0 0', color: '#334155', lineHeight: 1.8, fontSize: 16 }}>
                    {paragraph}
                  </p>
                ))}
                {section.bullets?.length ? (
                  <ul style={{ margin: '16px 0 0 1.1rem', color: '#334155', lineHeight: 1.85, fontSize: 16 }}>
                    {section.bullets.map((bullet) => (
                      <li key={bullet} style={{ marginBottom: 6 }}>{bullet}</li>
                    ))}
                  </ul>
                ) : null}
              </section>
            ))}

            <section style={sectionCard}>
              <h2 style={{ margin: 0, fontSize: 28, letterSpacing: '-0.03em' }}>Important Disclaimer</h2>
              <p style={{ margin: '16px 0 0', color: '#334155', lineHeight: 1.8, fontSize: 16 }}>
                LogiSelect acts as a technology platform facilitating logistics planning, carrier selection and transport execution workflows. Unless expressly agreed in writing, LogiSelect is not a carrier, freight forwarder, customs broker or warehouse operator and accepts no responsibility for the physical transportation, handling or storage of goods.
              </p>
            </section>
          </div>

          <div style={{ display: 'grid', gap: 28, alignContent: 'start' }}>
            <section style={sideCard}>
              <div style={{ textAlign: 'center', marginBottom: 24 }}>
                <img src="/logiselect-logo.png" alt="LogiSelect logo" style={{ height: 52, width: 'auto', display: 'inline-block', marginBottom: 8 }} />
                <h2 style={{ margin: 0, fontSize: 24, color: '#0f172a', fontWeight: 800 }}>LogiSelect TMS</h2>
                <p style={{ margin: '6px 0 0', color: '#64748b', fontSize: 14 }}>Use the dedicated TMS link to sign in and work inside the system.</p>
              </div>
              <a href={tmsLoginUrl} style={{ ...ctaPrimary, width: '100%', boxSizing: 'border-box' }}>Open TMS</a>
            </section>

            <section style={sideCard}>
              <h3 style={{ margin: 0, fontSize: 24, color: '#0f172a', fontWeight: 800 }}>LogiSelect Pty Ltd</h3>
              <div style={{ marginTop: 14, color: '#334155', lineHeight: 1.75, fontSize: 18 }}>
                <div>ACN: 700403345</div>
                <div>ABN: 62700403345</div>
              </div>
            </section>
          </div>
        </div>

        <footer
          style={{
            marginTop: 28,
            textAlign: 'center',
            color: '#475569',
            fontSize: 13,
            lineHeight: 1.6,
            padding: '10px 8px 2px',
          }}
        >
          {ownershipFooterText}
        </footer>
      </div>
    </div>
  )
}